import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { supabase } from "@/integrations/supabase/client";
import AuthLayout from "@/components/auth/AuthLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { DEACTIVATED_MESSAGE, isDeactivated } from "@/lib/deactivated";


const LoginPage = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [banner, setBanner] = useState<string | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();

  // Pick up a flash message passed via navigate state (e.g. from /auth/callback
  // when a Google login collides with an existing email/password account).
  useEffect(() => {
    const state = location.state as { authMessage?: string } | null;
    if (state?.authMessage) {
      setBanner(state.authMessage);
      window.history.replaceState({}, "");
    }
  }, [location.state]);

  const handleGoogle = async () => {
    setGoogleLoading(true);
    const { error } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: { redirectTo: `${window.location.origin}/auth/callback?next=/post-login` },
    });
    if (error) {
      setGoogleLoading(false);
      toast({ title: "Google sign-in failed", description: error.message, variant: "destructive" });
    }
  };


  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) {
      setLoading(false);
      toast({ title: "Login failed", description: error.message, variant: "destructive" });
      return;
    }

    const userId = data.user?.id;
    if (userId) {
      // A rejected account is deactivated: sign it straight back out.
      const { data: profile } = await supabase.from("profiles").select("status").eq("id", userId).maybeSingle();
      if (isDeactivated(profile?.status)) {
        await supabase.auth.signOut();
        setBanner(DEACTIVATED_MESSAGE);
        setLoading(false);
        return;
      }

      // Resolve destination here to avoid a transitional loading screen.
      // Pending users still get in — the app enforces the trial limits.
      const { data: tm } = await supabase
        .from("team_members")
        .select("brand_id")
        .eq("user_id", userId)
        .limit(1)
        .maybeSingle();

      let destination = "/onboarding";
      if (tm?.brand_id) {
        const { data: brand } = await supabase
          .from("brands")
          .select("profile_completed_at")
          .eq("id", tm.brand_id)
          .maybeSingle();
        if (brand?.profile_completed_at) destination = "/discover";
      }

      setLoading(false);
      navigate(destination, { replace: true });
      return;
    }

    setLoading(false);
    navigate("/discover");
  };

  return (
    <AuthLayout tagline="Welcome back, your communities are waiting.">
      <Helmet>
        <title>Log in — ernest</title>
        <meta name="description" content="Log in to ernest to manage your brand's community sponsorships and activations." />
      </Helmet>
      <div className="space-y-6">

        <div>
          <h2 className="font-semibold tracking-tight text-2xl text-[#0A0A0A]">Log in to ernest</h2>
        </div>

        {banner && (
          <div className="rounded-lg border border-[#E5E5E5] bg-[#FAFAF8] p-3 text-sm text-[#0A0A0A]">
            {banner}
          </div>
        )}

        {/* Google login hidden for now — logic preserved in handleGoogle */}


        <form onSubmit={handleLogin} className="space-y-3 md:space-y-4">
          <div className="space-y-2">
            <Label htmlFor="email" className="text-sm text-[#0A0A0A]">Work email</Label>
            <Input
              id="email"
              type="email"
              placeholder="you@company.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="h-11 border-[#E5E5E5] bg-white text-[#0A0A0A] placeholder:text-[#999]" />

          </div>
          <div className="space-y-2">
            <Label htmlFor="password" className="text-sm text-[#0A0A0A]">Password</Label>
            <Input
              id="password"
              type="password"
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="h-11 border-[#E5E5E5] bg-white text-[#0A0A0A] placeholder:text-[#999]" />

          </div>

          <Button
            type="submit"
            disabled={loading}
            className="w-full h-11 bg-[#0A0A0A] text-white hover:bg-[#1A1A1A] text-sm font-medium">

            {loading ? "Logging in…" : "Log in"}
          </Button>
        </form>

        <div className="flex items-center justify-between text-sm !mt-2 md:!mt-6">
          <Link to="/forgot-password" className="font-medium hover:underline text-[#584dff] transition-colors">
            Forgot password?
          </Link>
        </div>

        <p className="text-center text-sm text-[#666]">
          No account yet?{" "}
          <Link to="/signup" className="font-medium hover:underline text-[#584dff]">
            Get started
          </Link>
        </p>
      </div>
    </AuthLayout>);

};

export default LoginPage;
